/**
 * @file usePickingHistory.ts
 * @description Журнал проведённых и отменённых заданий на сборку
 * (phase19.14, довесок к экрану A).
 *
 * В отличие от очереди (грузится целиком, фильтры на клиенте) журнал растёт
 * бесконечно: страницы приходят с сервера по `finishedAt desc`, кнопка
 * «Показать ещё» дописывает следующую к уже загруженным.
 *
 * Поиск по номеру — локальный по загруженным страницам: на складе журнал
 * открывают «посмотреть, что вчера собрали», а не искать по всей истории;
 * серверного поиска в контракте нет.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  pickingApi,
  type PickingHistoryStatus,
  type PickingTaskListItemDto,
} from "../../../lib/api-picking";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Чипсы журнала. */
export type HistoryTab = "all" | "done" | "cancelled";

/** Реестр «чипс → статус запроса»: новая вкладка = запись, не ветка. */
const STATUS_BY_TAB: Record<HistoryTab, PickingHistoryStatus | undefined> = {
  all: undefined,
  done: "DONE",
  cancelled: "CANCELLED",
};

const PAGE_SIZE = 20;

export function usePickingHistory() {
  const [items, setItems] = useState<PickingTaskListItemDto[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [tab, setTab] = useState<HistoryTab>("all");
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (nextPage: number) => {
      const first = nextPage === 1;
      if (first) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await pickingApi.history({
          status: STATUS_BY_TAB[tab],
          warehouseId: warehouseId || undefined,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        // Первая страница заменяет список, следующие дописываются.
        setItems((prev) => (first ? res.items : [...prev, ...res.items]));
        setTotal(res.total);
        setPage(res.page);
        setError(null);
      } catch (err) {
        setError(wmsErrorText(err, "Не удалось загрузить журнал заданий"));
      } finally {
        if (first) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [tab, warehouseId],
  );

  // Смена вкладки или склада — запрос с первой страницы (фильтр серверный).
  useEffect(() => {
    void load(1);
  }, [load]);

  useEffect(() => {
    warehousesApi
      .list()
      .then(setWarehouses)
      .catch(() => setWarehouses([]));
  }, []);

  const visible = useMemo(() => {
    const needle = search.trim().toUpperCase();
    if (!needle) return items;
    return items.filter(
      (t) =>
        t.orderNumber.toUpperCase().includes(needle) ||
        t.number.toUpperCase().includes(needle),
    );
  }, [items, search]);

  return {
    warehouses,
    tab,
    setTab,
    warehouseId,
    setWarehouseId,
    search,
    setSearch,
    visible,
    total,
    loading,
    loadingMore,
    error,
    /** Есть ли что догружать (сравниваем с полным `total` сервера). */
    hasMore: items.length < total,
    loadMore: () => {
      void load(page + 1);
    },
    refresh: () => {
      void load(1);
    },
  };
}
